/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./styles/unified-theme.css"; // Unified orange-teal theme for entire website
import "./styles/page-specific.css"; // Page-specific customizations
import { initializeVisitorTracking } from "./utils/visitorTracking";
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Setup from "./pages/Setup";
import EmailVerification from "./pages/EmailVerification";
import CompleteSignup from "./pages/CompleteSignup";
import CommunityVerificationPage from "./pages/CommunityVerificationPage";
import ReferenceVerificationPage from "./pages/ReferenceVerificationPage";
import Community from "./pages/Community";
import JobSeekerDashboard from "./pages/JobSeekerDashboard";
import SmartResumeJobseeker from "./pages/SmartResumeJobseeker";
import MyProfile from "./pages/MyProfile";
import RecruiterDashboard from "./pages/RecruiterDashboard";
import RecruiterMyProfile from "./pages/RecruiterMyProfile";
import EmployerDashboard from "./pages/EmployerDashboard";
import EmployerMyProfile from "./pages/EmployerMyProfile";
import InternDashboard from "./pages/InternDashboardComplete";
import InternMyProfile from "./pages/InternMyProfile";
import UniversityDashboard from "./pages/UniversityDashboard";
import MoringaDashboard from "./pages/MoringaDashboard";
import UniversityRegistrationForm from "./pages/UniversityRegistrationForm";
import UniversityMyProfile from "./pages/UniversityMyProfile";
import InstituteDashboard from "./pages/InstituteDashboard";
import InstituteRegistrationForm from "./pages/InstituteRegistrationForm";
import AssociationDashboard from "./pages/AssociationDashboard";
import AssociationRegistrationForm from "./pages/AssociationRegistrationForm";
import AssociationMyProfile from "./pages/AssociationMyProfile";
import { ToastContainer } from "./components/Toast";
import NGODashboard from "./pages/NGODashboard";
import NGORegistrationForm from "./pages/NGORegistrationForm";
import NGOMyProfile from "./pages/NGOMyProfile";
import ModernJobDetails from "./pages/ModernJobDetails";
import HomePage from "./pages/HomePage";
import ExpoStudentRegistration from "./pages/ExpoStudentRegistration";
import FeaturedInternships from "./pages/FeaturedInternships";

import Header from "./components/Header";
import Footer from "./components/Footer";
import TermsOfService from "./pages/termsofservice";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AdminControlPanel from "./pages/AdminControlPanel";
import AdminDashboard from "./pages/AdminDashboard";
import CVBrowser from "./components/CVBrowser/index.jsx";
import OAuthSuccess from "./pages/OAuthSuccess";
import OAuthRoleSelection from "./pages/OAuthRoleSelection";
import OAuthError from "./pages/OAuthError";
import RecruiterRegistrationForm from "./pages/RecruiterRegistrationForm";
import EmployerRegistrationForm from "./pages/EmployerRegistrationForm";
import Company from "./pages/Company";
import Companies from "./pages/Companies";
import Universities from "./pages/Universities";
import Communities from "./pages/Communities";
import Associations from "./pages/Associations";
import Volunteers from "./pages/Volunteers";
import Professional from "./pages/Professional";
import NGOs from "./pages/NGOs";
import Events from "./pages/Events";
import Gallery from "./pages/Gallery";
import JobSeekerRegistrationForm from "./pages/JobSeekerRegistrationFormComprehensive";
import InternRegistrationForm from "./pages/InternRegistrationForm";
import RoleSetupChoice from "./pages/JobSeekerSetupChoice";
import PromoCodePage from "./pages/PromoCodePage";
import PostJob from "./pages/PostJob";
import PostInternship from "./pages/PostInternship";
import SalaryGuide from "./pages/SalaryGuide";
import CareerAdvice from "./pages/CareerAdvice";
import RecruitmentSolutions from "./pages/RecruitmentSolutions";
import Resources from "./pages/Resources";
import ContactSales from "./pages/ContactSales";
import Pricing from "./pages/Pricing";
import HelpCenter from "./pages/HelpCenter";
import ContactUs from "./pages/ContactUs";
import CookiePolicy from "./pages/CookiePolicy";
import ReferenceVerification from "./pages/ReferenceVerification";
import ReferenceVerificationStatus from "./pages/ReferenceVerificationStatus";
import AboutUs from "./pages/AboutUs";
import AboutPlatform from "./pages/AboutPlatform";
import MentorshipPrograms from "./pages/MentorshipPrograms";
import Scholarships from "./pages/Scholarships";
import TrainingPrograms from "./pages/TrainingPrograms";
import Careers from "./pages/Careers";
import Press from "./pages/Press";
import Partners from "./pages/Partners";
import Sitemap from "./pages/Sitemap";
import Accessibility from "./pages/Accessibility";
import Security from "./pages/Security";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { ProfilePhotoProvider } from "./context/ProfilePhotoContext";
import { LoadingProvider } from "./context/LoadingContext";

// Dashboard Redirect Component - Routes users to their role-specific dashboard
const DashboardRedirect = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Check if user has completed profile
    const hasCompletedProfile = user.profileCompleted || user.hasCompletedProfile;

    // Route based on role
    if (user.role === 'jobSeeker') {
      if (!hasCompletedProfile) {
        navigate('/setup-choice');
      } else {
        navigate('/jobseeker-dashboard');
      }
    } else if (user.role === 'recruiter') {
      if (!hasCompletedProfile) {
        navigate('/recruiter-registration');
      } else {
        navigate('/recruiter-dashboard');
      }
    } else if (user.role === 'employer') {
      if (!hasCompletedProfile) {
        navigate('/employer-registration');
      } else {
        navigate('/employer-dashboard');
      }
            } else if (user.role === 'intern') {
      if (!hasCompletedProfile) {
        navigate('/setup-choice');
      } else {
        navigate('/intern-dashboard');
      }
    } else if (user.role === 'university') {
      if (!hasCompletedProfile) {
        navigate('/university-registration');
      } else {
        navigate('/university-dashboard');
      }
    } else if (user.role === 'institute') {
      if (!hasCompletedProfile) {
        navigate('/institute-registration');
      } else {
        navigate('/institute-dashboard');
      }
    } else if (user.role === 'association') {
      if (!hasCompletedProfile) {
        navigate('/association-registration');
      } else {
        navigate('/association-dashboard');
      }
    } else if (user.role === 'ngo') {
      if (!hasCompletedProfile) {
        navigate('/ngo-registration');
      } else {
        navigate('/ngo-dashboard');
      }
    } else if (user.role === 'admin') {
      navigate('/admin');
    } else {
      navigate('/');
    }
  }, [user, navigate]);

  return <div style={{ padding: '50px', textAlign: 'center' }}>
    <h2>Redirecting to your dashboard...</h2>
  </div>;
};

// Settings Redirect Component
const SettingsRedirect = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role === 'jobSeeker') {
      navigate('/jobseeker-settings');
    } else if (user?.role === 'recruiter') {
      navigate('/recruiter-settings');
    } else {
      navigate('/');
    }
  }, [user, navigate]);

  return <div>Redirecting to settings...</div>;
};

// Lightweight redirect component to avoid HMR issues with <Navigate /> in dev
const Redirect = ({ to, replace = false }) => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace });
  }, [navigate, to, replace]);
  return null;
};

// Preserve search state when redirecting /jobs -> /jobseeker-dashboard
const JobsRedirect = () => {
  const location = useLocation();
  const state = location.state || {};
  return <Navigate to="/jobseeker-dashboard" state={state} replace />;
};

// Create a QueryClient instance for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: true, // Refetch when network reconnects
      retry: 1, // Retry failed requests once
      staleTime: 2 * 60 * 1000, // Data is fresh for 2 minutes
    },
  },
});

function App() {
  // Initialize visitor tracking on app load
  useEffect(() => {
    initializeVisitorTracking();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <AuthProvider>
          <NotificationProvider>
            <ProfilePhotoProvider>
              <LoadingProvider>
                <div className="app-container">
          <Routes>
            {/* Public routes without Header */}
            <Route path="/" element={<HomePage/>}/>
            <Route path="/expo/register" element={<ExpoStudentRegistration />} />
            <Route path="/career-expo/register" element={<ExpoStudentRegistration />} />
            <Route path="/jobs" element={<JobsRedirect />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/complete-signup" element={<CompleteSignup />} />
            <Route path="/verify-user" element={<CommunityVerificationPage />} />
            <Route path="/reference-verification" element={
              <ProtectedRoute>
                <>
                  <Header />
                  <ReferenceVerification />
                  <Footer />
                </>
              </ProtectedRoute>
            } />
            <Route path="/recruiter-registration" element={<RecruiterRegistrationForm />} />
            <Route path="/employer-registration" element={<EmployerRegistrationForm />} />
            <Route path="/intern-registration" element={<InternRegistrationForm />} />
            <Route path="/university-registration" element={<UniversityRegistrationForm />} />
            <Route path="/institute-registration" element={<InstituteRegistrationForm />} />
            <Route path="/association-registration" element={<AssociationRegistrationForm />} />
            <Route path="/ngo-registration" element={<NGORegistrationForm />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardRedirect />
              </ProtectedRoute>
            } />
            <Route path="/setup-choice" element={
              <ProtectedRoute>
                <RoleSetupChoice />
              </ProtectedRoute>
            } />
            <Route path="/jobseeker-registration" element={<JobSeekerRegistrationForm />} />
            <Route path="/complete-profile" element={<JobSeekerRegistrationForm />} />
            <Route path="/login" element={<Login />} />
            {/* OAuth routes - separate from normal login/signup */}
            <Route path="/oauth-role-selection" element={<OAuthRoleSelection />} />
            <Route path="/oauth-success" element={<OAuthSuccess />} />
            <Route path="/oauth-error" element={<OAuthError />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/terms" element={<TermsOfService/>}/>
            <Route path="/blog" element={<Blog/>}/>
            <Route path="/blog/:id" element={<BlogDetail/>}/>
            <Route path="/featured-internships" element={
              <>
                <Header />
                <FeaturedInternships />
                <Footer />
              </>
            }/>
            <Route path="/about" element={<AboutPlatform />} />
            <Route path="/about-platform" element={<AboutPlatform />} />
            <Route path="/about-name" element={<AboutPlatform />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/universities" element={<Universities />} />
            <Route path="/communities" element={<Communities />} />
            <Route path="/associations" element={<Associations />} />
            <Route path="/volunteers" element={<Volunteers />} />
            <Route path="/ngos" element={<NGOs />} />
            <Route path="/professional" element={<Professional />} />
            <Route path="/events" element={<Events />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/salary-guide" element={<SalaryGuide />} />
            <Route path="/career-advice" element={<CareerAdvice />} />
            <Route path="/recruitment-solutions" element={<RecruitmentSolutions />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/contact-sales" element={<ContactSales />} />
            <Route path="/pricing" element={
              <>
                <Header />
                <Pricing />
                <Footer />
              </>
            } />
            <Route path="/help-center" element={<HelpCenter />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/verify-reference/:token" element={<ReferenceVerificationPage />} />
            <Route path="/reference-verification-status/:referenceId" element={
              <ProtectedRoute>
                <>
                  <Header />
                  <ReferenceVerificationStatus />
                  <Footer />
                </>
              </ProtectedRoute>
            } />
            <Route path="/mentorship-programs" element={
              <>
                <Header />
                <MentorshipPrograms />
                <Footer />
              </>
            } />
            <Route path="/scholarships" element={
              <>
                <Header />
                <Scholarships />
                <Footer />
              </>
            } />
            <Route path="/training-programs" element={
              <>
                <Header />
                <TrainingPrograms />
                <Footer />
              </>
            } />
            <Route path="/careers" element={<Careers />} />
            <Route path="/press" element={
              <>
                <Header />
                <Press />
                <Footer />
              </>
            } />
            <Route path="/partners" element={
              <>
                <Header />
                <Partners />
                <Footer />
              </>
            } />
            <Route path="/sitemap" element={
              <>
                <Header />
                <Sitemap />
                <Footer />
              </>
            } />
            <Route path="/accessibility" element={
              <>
                <Header />
                <Accessibility />
                <Footer />
              </>
            } />
            <Route path="/security" element={
              <>
                <Header />
                <Security />
                <Footer />
              </>
            } />
            <Route path="/privacy-policy" element={<PrivacyPolicy/>}/>
            <Route path="/privacy" element={<PrivacyPolicy/>}/>
            
            {/* Protected routes with role-based access */}
            
            <Route path="/promo-codes" element={
              <ProtectedRoute>
                <PromoCodePage />
              </ProtectedRoute>
            }/>
            <Route path="/promo-code" element={
              <ProtectedRoute>
                <PromoCodePage />
              </ProtectedRoute>
            } />
            
                         {/* Recruiter-only routes */}
             <Route path="/recruiter-dashboard" element={
               <ProtectedRoute requiredRole="recruiter">
                 <RecruiterDashboard />
               </ProtectedRoute>
             } />
             <Route path="/admin/recruiter-dashboard/:id" element={
               <ProtectedRoute requiredRole="admin">
                 <RecruiterDashboard />
               </ProtectedRoute>
             } />
             <Route path="/recruiter-my-profile" element={
               <ProtectedRoute requiredRole="recruiter">
                 <RecruiterMyProfile />
               </ProtectedRoute>
             } />
             <Route path="/recruiter-my-profile/:id" element={
               <ProtectedRoute requiredRole="admin">
                 <RecruiterMyProfile />
               </ProtectedRoute>
             } />
             
             {/* Employer-only routes */}
             <Route path="/employer-dashboard" element={
               <ProtectedRoute requiredRole="employer">
                 <EmployerDashboard />
               </ProtectedRoute>
             } />
             <Route path="/admin/employer-dashboard/:id" element={
               <ProtectedRoute requiredRole="admin">
                 <EmployerDashboard />
               </ProtectedRoute>
             } />
            <Route path="/employer-my-profile" element={
              <ProtectedRoute requiredRole="employer">
                <EmployerMyProfile />
              </ProtectedRoute>
            } />
            <Route path="/employer-my-profile/:id" element={
              <ProtectedRoute requiredRole="admin">
                <EmployerMyProfile />
              </ProtectedRoute>
            } />
             
             
            <Route path="/post-job" element={
              <ProtectedRoute requiredRole={["recruiter", "employer", "association"]}>
                <PostJob />
              </ProtectedRoute>
            } />
            
            <Route path="/post-internship" element={
              <ProtectedRoute requiredRole={["recruiter", "employer", "association"]}>
                <PostInternship />
              </ProtectedRoute>
            } />
             
             <Route path="/company" element={
               <ProtectedRoute requiredRole={["recruiter", "employer"]}>
                 <>
                   <Company />
                 </>
               </ProtectedRoute>
             } />
             
             {/* Job Seeker-only routes */}
             <Route path="/jobseeker-dashboard" element={
               <ProtectedRoute requiredRole="jobSeeker">
                 <JobSeekerDashboard />
               </ProtectedRoute>
             } />
             <Route path="/admin/jobseeker-dashboard/:id" element={
               <ProtectedRoute requiredRole="admin">
                 <JobSeekerDashboard />
               </ProtectedRoute>
             } />
            <Route path="/profile" element={
              <ProtectedRoute requiredRole="jobSeeker">
                <MyProfile />
              </ProtectedRoute>
            } />
            <Route path="/profile/:id" element={
              <ProtectedRoute requiredRole={["admin", "recruiter", "employer", "association"]}>
                <MyProfile />
              </ProtectedRoute>
            } />
             
             
             {/* Intern-only routes */}
             <Route path="/intern-dashboard" element={
               <ProtectedRoute requiredRole="intern">
                 <InternDashboard />
               </ProtectedRoute>
             } />
             <Route path="/admin/intern-dashboard/:id" element={
               <ProtectedRoute requiredRole="admin">
                 <InternDashboard />
               </ProtectedRoute>
             } />
             <Route path="/intern-profile" element={
               <ProtectedRoute requiredRole="intern">
                 <InternMyProfile />
               </ProtectedRoute>
             } />
            <Route path="/intern-profile/:id" element={
              <ProtectedRoute requiredRole={["admin", "recruiter", "employer", "association"]}>
                <InternMyProfile />
              </ProtectedRoute>
            } />
            
            {/* University-only routes */}
            <Route path="/university-dashboard" element={
              <ProtectedRoute requiredRole="university">
                <UniversityDashboard />
              </ProtectedRoute>
            } />
            <Route path="/moringa-dashboard" element={
              <ProtectedRoute requiredRole="university">
                <MoringaDashboard />
              </ProtectedRoute>
            } />
            <Route path="/university-my-profile" element={
              <ProtectedRoute requiredRole="university">
                <UniversityMyProfile />
              </ProtectedRoute>
            } />
            <Route path="/university-my-profile/:id" element={
              <ProtectedRoute requiredRole="admin">
                <UniversityMyProfile />
              </ProtectedRoute>
            } />

            {/* Institute-only routes */}
            <Route path="/institute-dashboard" element={
              <ProtectedRoute requiredRole="institute">
                <InstituteDashboard />
              </ProtectedRoute>
            } />

            {/* Association-only routes */}
            <Route path="/association-dashboard" element={
              <ProtectedRoute requiredRole="association">
                <AssociationDashboard />
              </ProtectedRoute>
            } />
            <Route path="/association-my-profile" element={
              <ProtectedRoute requiredRole="association">
                <AssociationMyProfile />
              </ProtectedRoute>
            } />
            <Route path="/association-my-profile/:id" element={
              <ProtectedRoute requiredRole="association">
                <AssociationMyProfile />
              </ProtectedRoute>
            } />

            {/* NGO-only routes */}
            <Route path="/ngo-dashboard" element={
              <ProtectedRoute requiredRole="ngo">
                <NGODashboard />
              </ProtectedRoute>
            } />
            <Route path="/ngo-my-profile" element={
              <ProtectedRoute requiredRole="ngo">
                <NGOMyProfile />
              </ProtectedRoute>
            } />
            
            {/* Admin-only routes */}
            <Route path="/admin" element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/admin/control-panel" element={
              <ProtectedRoute requiredRole="admin">
                <AdminControlPanel />
              </ProtectedRoute>
            } />
            
            
            {/* Other protected routes */}
            <Route path="/job-details/:jobId" element={<ModernJobDetails />} />
            <Route path="/job-details" element={<ModernJobDetails />} />
            <Route path="/smart-resume" element={
              <ProtectedRoute requiredRole={["job_seeker", "jobseeker"]}>
                <SmartResumeJobseeker />
              </ProtectedRoute>
            } />

            <Route path="/settings" element={
              <ProtectedRoute>
                <SettingsRedirect />
              </ProtectedRoute>
            }/>
            <Route path="/community" element={
              <ProtectedRoute>
                <>
                  <Header />
                  <Community />
                  <Footer />
                </>
              </ProtectedRoute>
            }/>
            <Route path="/cv-browser" element={
              <ProtectedRoute requiredRole={["recruiter", "employer"]}>
                <>
                  <Header />
                  <CVBrowser />
                  <Footer />
                </>
              </ProtectedRoute>
            } />
            
            {/* OAuth Routes */}
            <Route path="/oauth-success" element={<OAuthSuccess />} />
            <Route path="/oauth-role-selection" element={<OAuthRoleSelection />} />
          </Routes>
        </div>
        <ToastContainer />
            </LoadingProvider>
          </ProfilePhotoProvider>
        </NotificationProvider>
      </AuthProvider>
      
    </Router>
    </QueryClientProvider>
  );
}

export default App;
